var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { hasValue, isOfflineDeployment } from '@utils';
import { UXPContext } from "@core/context/Context";
import Modal from '../widget/popups/Modal';
import Button from '@widget/buttons/Button';
import classNames from 'classnames';
import useAlert from '@notifications/Alerts/useAlerts';
function encodeJWT(payload, secret, options) {
    return __awaiter(this, void 0, void 0, function* () {
        const fullPayload = Object.assign({}, payload);
        if (options === null || options === void 0 ? void 0 : options.expiresIn) {
            const match = options.expiresIn.match(/^(\d+)(s|m|h|d)$/);
            if (match) {
                const multipliers = { s: 1, m: 60, h: 3600, d: 86400 };
                fullPayload.exp = Math.floor(Date.now() / 1000) + parseInt(match[1]) * multipliers[match[2]];
            }
        }
        const b64url = (s) => btoa(s).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
        const header = b64url(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
        const body = b64url(JSON.stringify(fullPayload));
        const enc = new TextEncoder();
        const key = yield crypto.subtle.importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
        const sig = yield crypto.subtle.sign('HMAC', key, enc.encode(`${header}.${body}`));
        const sigB64 = b64url(String.fromCharCode(...new Uint8Array(sig)));
        return `${header}.${body}.${sigB64}`;
    });
}
const marketplaceUrl = 'https://marketplace.spaceworx.com';
const secret = 'spaceworx-lucy-jwt-secret';
/**
 * @export
 * This component gives a styled button to go to the marketplace product pages
 * Intend to use in the configuration panels and other place
 *
 * @example
 *
 * ```
 * <BuyOnSpaceworxButton productIds={['610b6a0583892800ab104e82']} />
 * ```
 *
 * @advanced
 */
const BuyOnSpaceworxButton = (props) => {
    const { link, className, styles, productIds } = props;
    const context = React.useContext(UXPContext);
    const [showNotice, setShowNotice] = React.useState(false);
    const [time, setTime] = React.useState(7);
    const timerRef = React.useRef(null);
    const alerts = useAlert();
    React.useEffect(() => {
        timerRef.current = null;
    }, [props]);
    React.useEffect(() => {
        if (showNotice) {
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                console.log('time ', time);
                if (time === 0) {
                    clearInterval(timerRef.current);
                    openLink();
                    return;
                }
                setTime(prev => (prev - 1));
            }, 1000);
        }
    }, [time, showNotice]);
    function openLink() {
        return __awaiter(this, void 0, void 0, function* () {
            // link is deprecated - use product ids instead
            if ((productIds === null || productIds === void 0 ? void 0 : productIds.length) > 0) {
                let userDetails = null;
                try {
                    userDetails = yield context.getUserDetails();
                }
                catch (error) {
                    console.log('unable to get user details ', error);
                }
                const payload = {
                    firstName: (userDetails === null || userDetails === void 0 ? void 0 : userDetails.FirstName) || '',
                    lastName: '',
                    email: (userDetails === null || userDetails === void 0 ? void 0 : userDetails.Email) || '',
                    productIds: productIds
                };
                const token = yield encodeJWT(payload, secret, { expiresIn: '1h' });
                const url = new URL('lucy-products', marketplaceUrl);
                url.searchParams.set('token', token);
                url.searchParams.set('source', 'lucy');
                window.open(url.href, '_blank');
            }
            // remove later
            else if (hasValue(link)) {
                window.open(link, '_blank');
            }
            setShowNotice(false);
        });
    }
    function handleOpenLink() {
        let hasPopupShowed = hasValue(localStorage.getItem('lucy-spaceworx-redirect-popup'));
        if (hasPopupShowed)
            openLink();
        else {
            setShowNotice(true);
            localStorage.setItem('lucy-spaceworx-redirect-popup', '1');
            setTime(7);
        }
    }
    // the marketplace is an internet service - hide the button on air-gapped deployments
    if (isOfflineDeployment())
        return null;
    if (!hasValue(link) && (productIds === null || productIds === void 0 ? void 0 : productIds.length) == 0)
        return null;
    return _jsxs(_Fragment, { children: [_jsxs("div", { className: classNames('uxp-buy-on-spaceworx-btn', className), style: styles || {}, onClick: handleOpenLink, children: [_jsx("div", { className: "icon-container", children: _jsx("div", { className: "icon" }) }), _jsx("div", { className: "text", children: context.$L('uxp-core.text.buy-on-spaceworx') })] }), _jsx(Modal, { show: showNotice, className: 'uxp-buy-on-spaceworx-redirect-modal', autoSize: true, children: _jsxs("div", { className: "content", children: [_jsxs("div", { className: "msg", children: [context.$L('uxp-core.text.marketplace-redirect-description'), _jsx("div", { className: 'disclaimer', children: context.$L('uxp-core.text.marketplace-redirect-disclaimer') }), _jsxs("div", { className: "time", children: [" ", context.$L('uxp-core.text.marketplace-redirect-timer', { time }), " "] })] }), _jsxs("div", { className: "actions", children: [_jsx(Button, { title: context.$L('uxp-core.text.cancel'), onClick: () => setShowNotice(false), className: 'cancel-btn' }), _jsx(Button, { title: context.$L('uxp-core.text.continue'), onClick: openLink, className: 'continue-btn', active: true })] })] }) })] });
};
/**
 * This gives a pre defined component to used in configuration panels where we need to explain what is spaceworx is
 *
 * @export
 *
 *
 * @example
 * ```
 * <SpaceworxDescriptionTag />
 * ```
 *
 * @advanced
 */
const SpaceworxDescriptionTag = (props) => {
    const context = React.useContext(UXPContext);
    const { className, styles } = props;
    return _jsxs("div", { className: classNames('uxp-spaceworx-description-tag', className), style: styles || {}, children: [" ", context.$L('uxp-core.text.marketplace-description'), " "] });
};
export { BuyOnSpaceworxButton, SpaceworxDescriptionTag };
